import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, BookOpen, Zap, Flame, Trophy, Shield, Star, Clock, X, Crown, ArrowRight, CreditCard, Package, Calendar, TrendingUp, LogOut, Pencil, Check, Settings } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const isNios = user?.school === 'nios';
  const { plan } = useSubscription();
  const nav = useNavigate();
  const [cancelLoading, setCancelLoading] = useState(false);
  const [credits, setCredits]             = useState(user?.credits || 0);
  // Name editing
  const [editingName, setEditingName]     = useState(false);
  const [nameInput, setNameInput]         = useState(user?.name || '');
  const [nameSaving, setNameSaving]       = useState(false);
  const [nameCooldown, setNameCooldown]   = useState(null); // days remaining

  useEffect(() => {
    axios.get(`${API}/credits`, { withCredentials: true })
      .then(r => setCredits(r.data?.credits ?? user?.credits ?? 0)).catch((e) => { console.warn('Credits fetch failed:', e); });
  }, [isNios]);

  const handleSaveName = async () => {
    if (!nameInput.trim() || nameInput.trim() === user?.name) { setEditingName(false); return; }
    setNameSaving(true);
    try {
      const { data } = await axios.put(`${API}/auth/update-name`, { name: nameInput.trim() }, { withCredentials: true });
      setUser(prev => ({ ...prev, name: data.name }));
      toast.success('Name updated!');
      setEditingName(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || '';
      if (e?.response?.status === 429) {
        setNameCooldown(detail);
        setEditingName(false);
        toast.error(detail);
      } else {
        toast.error(detail || 'Could not update name.');
      }
    }
    setNameSaving(false);
  };

  const handleCancelPlan = async () => {
    if (!window.confirm('Cancel your subscription? You will revert to the free plan at the end of the billing period.')) return;
    setCancelLoading(true);
    try {
      await axios.post(`${API}/subscription/cancel`, {}, { withCredentials: true });
      toast.success('Subscription cancelled. You will retain access until the billing period ends.');
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not cancel subscription');
    }
    setCancelLoading(false);
  };

  const xp = user?.xp || 0;
  const level = Math.max(1, Math.floor(xp / 500) + 1);
  const xpInLevel = xp % 500;
  // School display name helper
  const SCHOOL_NAMES = {
    'nios': 'NIOS — National Institute of Open Schooling',
    'brooklyn_national': 'Brooklyn National Public School',
    'national_public':   'National Public School',
  };

  const infoItems = [
    { label: 'Email', value: user?.email, icon: Mail, color: '#22d3ee' },
    { label: 'Member Since', value: user?.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Recently', icon: Clock, color: '#8b5cf6' },
    { label: 'Auth Type', value: user?.auth_type === 'google' ? 'Google' : 'Email & Password', icon: Shield, color: '#10b981' },
    { label: 'Role', value: user?.role === 'admin' ? 'Administrator' : 'Student', icon: Star, color: '#f59e0b' },
    ...(user?.school ? [{ label: 'School', value: SCHOOL_NAMES[user.school] || user.school, icon: BookOpen, color: '#f472b6', note: 'To change school, contact admin' }] : []),
  ];

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div><h1 className="text-2xl sm:text-3xl font-heading font-black text-white flex items-center gap-3">
          <User size={28} className="text-cyan-400" />
          Profile
        </h1>
        <p className="text-zinc-500 text-sm font-body mt-1">Your AceIt AI account</p></div>
        <button type="button" onClick={() => nav('/settings')} aria-label="Open settings" title="Settings" data-testid="profile-settings" className="flex items-center gap-2 rounded-xl border border-white/10 px-3 py-2 text-sm text-white hover:bg-white/5 transition-colors"><Settings size={19} /><span>Settings</span></button>
      </div>

      {/* Avatar & Name */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="glass rounded-2xl p-6 border border-white/10 text-center">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-400 to-violet-600 flex items-center justify-center text-black font-heading font-black text-3xl mx-auto mb-3">
          {(editingName ? nameInput : user?.name)?.[0]?.toUpperCase() || 'S'}
        </div>

        {/* Editable name */}
        {editingName ? (
          <div className="flex items-center justify-center gap-2 mt-1">
            <input
              data-testid="name-edit-input"
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
              maxLength={40}
              autoFocus
              className="bg-zinc-800 border border-cyan-500/40 text-white text-center rounded-lg px-3 py-1.5 text-sm font-heading font-bold focus:outline-none focus:border-cyan-400 w-44"
            />
            <button onClick={handleSaveName} disabled={nameSaving} data-testid="name-save-btn"
              className="p-1.5 rounded-lg bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 transition-all disabled:opacity-50">
              {nameSaving ? <div className="w-3.5 h-3.5 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" /> : <Check size={14} />}
            </button>
            <button onClick={() => { setEditingName(false); setNameInput(user?.name || ''); }} data-testid="name-cancel-btn"
              className="p-1.5 rounded-lg text-zinc-500 hover:text-white hover:bg-white/5 transition-all">
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 mt-1">
            <h2 className="text-white font-heading font-black text-xl">{user?.name}</h2>
            {nameCooldown ? (
              <span className="text-zinc-600 text-[10px] font-body">{nameCooldown}</span>
            ) : (
              <button onClick={() => { setNameInput(user?.name || ''); setEditingName(true); }}
                data-testid="name-edit-btn"
                title="Change name (once every 7 days)"
                className="p-1 rounded-md text-zinc-600 hover:text-cyan-400 hover:bg-cyan-400/10 transition-all">
                <Pencil size={13} />
              </button>
            )}
          </div>
        )}
        <p className="text-zinc-500 text-sm font-body mt-0.5">
          {user?.school === 'nios' ? 'Secondary Course Student' : `Class ${user?.class_level} Student`}
        </p>

        {/* Level Badge */}
        <div className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full bg-amber-400/15 border border-amber-400/25">
          <Zap size={15} className="text-amber-400" />
          <span className="text-amber-400 font-heading font-bold">Level {level}</span>
          <span className="text-amber-600 text-xs font-body">•</span>
          <span className="text-amber-500 text-sm font-body">{xp} XP</span>
        </div>

        {/* XP Progress */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-zinc-600 text-xs font-body">Level {level}</span>
            <span className="text-zinc-600 text-xs font-body">Level {level + 1}</span>
          </div>
          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
            <motion.div className="h-full rounded-full" style={{ background: 'linear-gradient(90deg, #f59e0b, #d946ef)' }}
              initial={{ width: 0 }} animate={{ width: `${(xpInLevel / 500) * 100}%` }} transition={{ duration: 1, delay: 0.3 }} />
          </div>
          <p className="text-zinc-600 text-xs font-body text-center mt-1">{xp % 500} / 500 XP to next level</p>
        </div>
      </motion.div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: 'Streak', value: `${user?.streak || 0} days`, icon: Flame, color: '#ef4444' },
          { label: 'Longest Streak', value: `${user?.longest_streak || 0} days`, icon: Trophy, color: '#f59e0b' },
        ].map(({ label, value, icon: Icon, color }, i) => (
          <motion.div key={label} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.15 + i * 0.05 }}
            className="glass-surface rounded-2xl p-4 border border-white/5">
            <div className="p-2 rounded-xl mb-2 inline-block" style={{ background: color + '20' }}>
              <Icon size={18} style={{ color }} />
            </div>
            <p className="text-xl font-heading font-bold text-white">{value}</p>
            <p className="text-zinc-500 text-xs font-body">{label}</p>
          </motion.div>
        ))}
      </div>

      {/* Account Info */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
        className="glass-surface rounded-2xl p-5 border border-white/5">
        <h3 className="text-white font-heading font-bold mb-4">Account Information</h3>
        <div className="space-y-3">
          {infoItems.map(({ label, value, icon: Icon, color, note }) => (
            <div key={label} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: color + '15' }}>
                <Icon size={15} style={{ color }} />
              </div>
              <div>
                <p className="text-zinc-500 text-xs font-body">{label}</p>
                <p className="text-white text-sm font-body">{value}</p>
                {note && <p className="text-zinc-600 text-[10px] font-body mt-0.5">{note}</p>}
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* ── Subscription Management ───────────────────────────────── */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
        className="glass-surface rounded-2xl p-5 border border-white/5" data-testid="subscription-section">
        <h3 className="text-white font-heading font-bold mb-4 flex items-center gap-2">
          <CreditCard size={18} className="text-violet-400" /> Subscription & Credits
        </h3>

        {/* Current Plan */}
        <div className="flex items-center justify-between mb-4 p-4 rounded-xl border"
          style={{ borderColor: plan?.color ? plan.color + '30' : 'rgba(255,255,255,0.08)', background: plan?.color ? plan.color + '08' : 'rgba(255,255,255,0.02)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: plan?.color ? plan.color + '20' : 'rgba(255,255,255,0.08)' }}>
              <Package size={18} style={{ color: plan?.color || '#94a3b8' }} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="text-white font-heading font-bold text-base capitalize">
                  {plan?.name || plan?.plan_id || 'Free'} Plan
                </p>
                {plan?.plan_id && plan.plan_id !== 'free' && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-heading font-bold bg-green-500/20 text-green-400 border border-green-500/20">ACTIVE</span>
                )}
              </div>
              <p className="text-zinc-500 text-xs font-body mt-0.5">
                {plan?.plan_id === 'free' ? 'Limited features — upgrade to unlock more' : plan?.tagline || 'Full access'}
              </p>
            </div>
          </div>
          {plan?.plan_id && plan.plan_id !== 'free' && plan?.price_monthly > 0 && (
            <p className="text-white font-heading font-bold text-sm">₹{plan.price_monthly}<span className="text-zinc-500 font-body text-xs">/mo</span></p>
          )}
        </div>

        {/* Credits */}
        <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-amber-500/8 border border-amber-500/15">
          <Zap size={16} className="text-amber-400" />
          <div className="flex-1">
            <p className="text-white text-sm font-body font-semibold">{credits} credits remaining</p>
            <p className="text-zinc-500 text-xs font-body">Credits are used for AI tutoring (2–10 per response)</p>
          </div>
        </div>

        {/* Renewal / billing info */}
        {plan?.renewal_date && (
          <div className="flex items-center gap-2 mb-4 text-xs font-body text-zinc-400">
            <Calendar size={13} />
            <span>Renews on <strong className="text-zinc-200">{new Date(plan.renewal_date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</strong></span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 flex-wrap">
          {plan?.plan_id === 'free' || !plan?.plan_id ? (
            <button data-ace-primary="true" onClick={() => nav('/pricing')}
              data-testid="upgrade-plan-btn"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-violet-500 hover:bg-violet-400 text-white font-heading font-bold text-sm transition-all">
              <Crown size={15} /> Upgrade Plan <ArrowRight size={14} />
            </button>
          ) : (
            <>
              <button onClick={() => nav('/pricing')}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-violet-500/30 bg-violet-500/10 hover:bg-violet-500/20 text-violet-300 font-heading font-bold text-sm transition-all">
                <TrendingUp size={15} /> Change Plan
              </button>
              <button onClick={handleCancelPlan} disabled={cancelLoading}
                data-testid="cancel-plan-btn"
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-red-500/20 bg-red-500/8 hover:bg-red-500/15 text-red-400 font-heading font-bold text-sm transition-all disabled:opacity-50">
                {cancelLoading ? <div className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" /> : <LogOut size={14} />}
                Cancel Plan
              </button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}




